// valibot, not zod: this schema ships in the client bundle, where valibot's tree-shaking keeps it small.
import * as v from 'valibot'

import { FirestoreTimestampSchema } from '~shared/core/firestore'
import type { FirestoreDocumentSnapshot } from '~shared/core/firestore'
import { IsoDateSchema } from '~shared/timeutils'

// =============== CANONICAL ENUMS
//
// Object-const style, not TypeScript `enum`.

export const ColumnType = {
  RICHTEXT: 'richtext',
  SELECT: 'select',
  /** @deprecated Legacy type, images are now nodes inside richtext columns. */
  IMAGES: 'images',
  /** @deprecated Legacy type, attachments are now nodes inside richtext columns. */
  ATTACHMENTS: 'attachments',
} as const

export type ColumnType = (typeof ColumnType)[keyof typeof ColumnType]

// =============== WIDTH
//
// Column widths in px. The default is what a new column is created with and
// what a document without a width parses to. The limits bound a resize.

export const COLUMN_DEFAULT_WIDTH = 200
export const COLUMN_MIN_WIDTH = 100
export const COLUMN_MAX_WIDTH = 800

// Enforced on writes only. Reads stay lenient so a legacy out-of-range width still parses.
const ColumnWidthSchema = v.pipe(v.number(), v.minValue(COLUMN_MIN_WIDTH), v.maxValue(COLUMN_MAX_WIDTH))

// =============== LEAF SCHEMAS

export const ColumnTypeSchema = v.picklist(Object.values(ColumnType))

// =============== FIRESTORE BOUNDARY
//
// The shape of `doc.data()` for a column document. Timestamps in, `Date`s out.
// `id` and the audit stamps (`createdAt`/`updatedAt`) come from the snapshot
// envelope, see {@link parseColumnSnapshot}. Every field is `nullish`-with-default
// so a legacy document that stores `null` (or omits the field) parses to a
// complete column instead of throwing.
//
// The schema is flat: `options` is accepted on every column because the stored
// document keeps it after a select column is converted to richtext. The parsers
// narrow it away, see {@link narrowColumn}.

export const ColumnFirestoreSchema = v.object({
  name: v.nullish(v.string(), ''),
  type: v.nullish(ColumnTypeSchema, ColumnType.RICHTEXT),
  // The width of the column in pixels.
  width: v.nullish(v.number(), COLUMN_DEFAULT_WIDTH),
  // The uid of the single user this column is private to, or `null` for a public column.
  privateUid: v.nullish(v.string(), null),
  options: v.nullish(v.array(v.string()), () => []),
  deletedAt: v.nullish(FirestoreTimestampSchema, null),
})

/** The raw Firestore document shape accepted by {@link ColumnFirestoreSchema}. */
export type ColumnFirestore = v.InferInput<typeof ColumnFirestoreSchema>

type ColumnFlat = v.InferOutput<typeof ColumnFirestoreSchema>

// =============== WIRE BOUNDARY
//
// The over-the-wire shape: ISO date strings and the system fields included.
// `options` is optional because a serialized richtext column omits it.

export const ColumnSerializedSchema = v.object({
  id: v.string(),
  name: v.string(),
  type: ColumnTypeSchema,
  // The width of the column in pixels.
  width: v.number(),
  // The uid of the single user this column is private to, or `null` for a public column.
  privateUid: v.nullish(v.string(), null),
  options: v.optional(v.array(v.string()), () => []),
  deletedAt: v.nullish(IsoDateSchema, null),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
})

/** The raw serialized shape accepted by {@link ColumnSerializedSchema}. */
export type ColumnSerialized = v.InferInput<typeof ColumnSerializedSchema>

// =============== WRITE BOUNDARY
//
// What a client may send to patch a column: an allowlist of wire fields, so
// system fields stay server-owned and a new field is not writable until listed
// here. Strict, so an unknown key is rejected rather than written. `privateUid`
// and `deletedAt` are deliberately absent.
//
// Flat on purpose: a patch cannot know the stored column's current type.

export const ColumnPatchSchema = v.strictObject({
  ...v.partial(v.pick(ColumnSerializedSchema, ['name', 'type', 'options'])).entries,
  width: v.optional(ColumnWidthSchema),
})

export type ColumnPatch = v.InferOutput<typeof ColumnPatchSchema>

/** What a client may seed a new column with. The rest comes from the defaults. */
export const ColumnCreateSchema = v.strictObject({
  name: v.optional(v.string()),
  type: v.optional(ColumnTypeSchema),
  // The private-notes request sends `width: null`, the default fills it.
  width: v.nullish(ColumnWidthSchema),
  privateUid: v.nullish(v.string()),
})

export type ColumnCreate = v.InferOutput<typeof ColumnCreateSchema>

// =============== CANONICAL DOMAIN TYPE

type ColumnBase = Omit<ColumnFlat, 'type' | 'options'>

/**
 * A column's own fields as a discriminated union: only a select column has
 * `options`, whatever the stored document holds.
 *
 * `options` is readonly so a column read from a valtio snapshot, which freezes
 * arrays, is still assignable to `Column`.
 */
export type ColumnFields = ColumnBase &
  (
    | { type: typeof ColumnType.SELECT; options: readonly string[] }
    | { type: Exclude<ColumnType, typeof ColumnType.SELECT> }
  )

/**
 * The canonical Column.
 *
 * Fully parsed, `Date`-typed, with system fields.
 */
export type Column = ColumnFields & {
  id: string
  createdAt: Date
  updatedAt: Date
}

export type SelectColumn = Extract<Column, { type: typeof ColumnType.SELECT }>

/** Branded id of {@link Column}. */
export type ColumnId = Column['id'] & { readonly __brand: 'ColumnId' }

// =============== PARSERS

/**
 * Turn the flat parsed fields into the union: `options` is kept for a select
 * column and dropped for every other type.
 */
function narrowColumn(flat: ColumnFlat): ColumnFields {
  const { options, ...rest } = flat
  if (rest.type === ColumnType.SELECT) return { ...rest, type: rest.type, options }
  return { ...rest, type: rest.type }
}

// The whole document body, audit stamps included, so one parse reports every issue.
const ColumnDocumentSchema = v.object({
  ...ColumnFirestoreSchema.entries,
  createdAt: v.nullish(FirestoreTimestampSchema),
  updatedAt: v.nullish(FirestoreTimestampSchema),
})

/**
 * Parse a Firestore `DocumentSnapshot` into a canonical column.
 *
 * The document body is validated by {@link ColumnFirestoreSchema}; `id` comes
 * from the snapshot and the audit stamps prefer an explicit document field, then
 * the snapshot metadata, then the epoch (`new Date(0)`). The web SDK has no
 * `createTime`, and column writes store no stamps, so on the client they are
 * the epoch.
 *
 * @throws {Error} naming the column and every issue when the body does not match.
 */
export function parseColumnSnapshot(snapshot: FirestoreDocumentSnapshot): Column {
  const result = v.safeParse(ColumnDocumentSchema, snapshot.data() ?? {})
  if (!result.success) throw new Error(`Invalid column document ${snapshot.id}:\n${v.summarize(result.issues)}`)
  const { createdAt, updatedAt, ...flat } = result.output

  return {
    ...narrowColumn(flat),
    id: snapshot.id,
    createdAt: createdAt ?? snapshot.createTime?.toDate() ?? new Date(0),
    updatedAt: updatedAt ?? snapshot.updateTime?.toDate() ?? new Date(0),
  }
}

/**
 * Parse a serialized column (ISO date strings) into a canonical column.
 *
 * @throws {ValiError} when the input does not match.
 */
export function parseColumnSerialized(input: unknown): Column {
  const { id, createdAt, updatedAt, ...flat } = v.parse(ColumnSerializedSchema, input)
  return { ...narrowColumn(flat), id, createdAt, updatedAt }
}

/**
 * Serialize a canonical column into the over-the-wire shape.
 *
 * `Date` does not survive JSON, so every date field is emitted as an ISO string
 * (or `null`). The result round-trips through {@link parseColumnSerialized}.
 */
export function serializeColumn(column: Column): ColumnSerialized {
  return {
    id: column.id,
    name: column.name,
    type: column.type,
    width: column.width,
    privateUid: column.privateUid,
    // Copied to a mutable array: the domain type is readonly, the wire type is not.
    ...(column.type === ColumnType.SELECT && { options: [...column.options] }),
    deletedAt: column.deletedAt?.toISOString() ?? null,
    createdAt: column.createdAt.toISOString(),
    updatedAt: column.updatedAt.toISOString(),
  }
}

// =============== ORDER

/**
 * Join the rundown's column order (`rundown.columns`) with column documents.
 *
 * The order decides what belongs to the rundown: an id without a document is
 * skipped, and a document whose id is not in the order is dropped.
 *
 * @param getById - Looks up a column by id, eg. a store getter or `map.get`
 */
export function sortColumnsByOrder<T extends Pick<Column, 'id'>>(
  order: readonly string[],
  getById: (id: string) => T | undefined,
): T[] {
  const columns: T[] = []

  for (const id of order) {
    const column = getById(id)
    if (column) columns.push(column)
  }

  return columns
}

// =============== ACCESS
//
// A `privateUid` that is `null` or empty means public.

/**
 * The column is visible to everyone: not private and not soft-deleted.
 *
 * A type guard that accepts a missing column, so it can gate a map lookup.
 */
export function isPublicColumn<T extends Pick<Column, 'privateUid' | 'deletedAt'>>(
  column: T | null | undefined,
): column is T {
  return !!column && !column.privateUid && column.deletedAt == null
}

/**
 * The column is the private column of `uid`. Always false without a uid.
 *
 * The name is not read: private columns are created as "Private notes", but
 * nothing depends on it.
 */
export function isOwnPrivateColumn(column: Pick<Column, 'privateUid'>, uid: string | null | undefined): boolean {
  return !!uid && column.privateUid === uid
}

/**
 * `uid` may see the column: it is not private, or it is their own. Does not
 * look at `deletedAt`.
 */
export function canAccessColumn(column: Pick<Column, 'privateUid'>, uid: string | null | undefined): boolean {
  return !column.privateUid || isOwnPrivateColumn(column, uid)
}

/**
 * The schema's default column fields (a richtext column). The single source of
 * defaults.
 */
export function getColumnSchemaDefaults(): ColumnFields {
  return narrowColumn(v.getDefaults(ColumnFirestoreSchema))
}
